/**
 * ecology.ts — WHAT grows where, by rule (2026-09-25, visual-realism R5). Pure, worker-safe.
 *
 * The biome scatter picked a species with `key = i % keys` at a uniform random position. Plants do
 * not grow like that: every species has a niche — a moisture band, a shade band (open meadow ↔ deep
 * wood), a slope it can hold, an altitude band, a need (or not) for open water — and the one best
 * suited to a spot wins it (Far Cry 5's viability rules; Unreal's procedural foliage "priority wins").
 * Viability also sets SIZE, so a stand shrinks toward the edge of its niche instead of stopping at
 * full height, and it gates by a fixed per-candidate hash (a density threshold — a tier change thins
 * the stand, it never reshuffles it). Positions come from CLUSTER SEEDS × a low-frequency noise
 * (clumps and gaps), not a uniform lottery.
 *
 * Also here: CLEARINGS (20–80 m holes inside deep stands — the woods' planter skips them and the grass
 * reads open ground there), the ECOTONE (the shrub band on a wood's edge) and RIPARIAN belts (reeds
 * 0–6 m from water, shrubs 6–25 m, in every biome).
 */
import type { Biome } from '@shared/terrain.js';
import { forestNoise } from './forest_field.js';

export type PlantRole = 'shrub' | 'herb' | 'reed' | 'fungus' | 'moss' | 'succulent';
export interface PlantTraits {
  key: string;
  role: PlantRole;
  /** Wetness band (0 dry … 1 waterlogged) the plant thrives in. */
  moist: [number, number];
  /** Shade band (the woods' density: 0 open … 1 deep stand). */
  shade: [number, number];
  /** Steepest ground it holds (rise/run). */
  slopeMax: number;
  /** Metres above the sea it grows between. */
  alt: [number, number];
  /** Scale range at full viability (a cooked asset is ~1–1.3 m). */
  scale: [number, number];
}
const T = (key: string, role: PlantRole, moist: [number, number], shade: [number, number], slopeMax: number, alt: [number, number], scale: [number, number]): PlantTraits =>
  ({ key: `foliage:${key}`, role, moist, shade, slopeMax, alt, scale });
const ANY_ALT: [number, number] = [0.5, 600];

/** The understory's species per biome with their niches (keys are the re-cooked groundcover set). */
export const UNDERSTORY: Record<Biome, PlantTraits[]> = {
  regolith_plains: [
    T('thorn_scrub_bush', 'shrub', [0, 0.45], [0, 0.55], 0.8, ANY_ALT, [0.7, 1.2]),
    T('plains_sage_shrub', 'shrub', [0.1, 0.6], [0, 0.35], 0.7, ANY_ALT, [0.8, 1.3]),
    T('berry_bush', 'shrub', [0.3, 0.9], [0.15, 1], 0.6, ANY_ALT, [0.7, 1.1]), // the wood's understory shrub
    T('dustbell_flower', 'herb', [0.15, 0.6], [0, 0.3], 0.6, ANY_ALT, [0.6, 1.0]),
    T('rippleweed_grass', 'herb', [0.2, 0.8], [0, 0.4], 0.7, ANY_ALT, [0.6, 1.0]),
    T('bristle_grass', 'herb', [0, 0.55], [0, 0.35], 0.8, ANY_ALT, [0.6, 1.0]),
    T('fern_bush', 'herb', [0.45, 1], [0.35, 1], 0.7, ANY_ALT, [0.6, 1.0]),
    T('regolith_bloom_cactus', 'succulent', [0, 0.25], [0, 0.2], 0.6, ANY_ALT, [0.6, 1.0]),
  ],
  crystal_forest: [
    T('quartz_bramble', 'shrub', [0.2, 0.7], [0, 0.85], 0.8, ANY_ALT, [0.7, 1.1]),
    T('luminous_orb_bush', 'shrub', [0.4, 0.9], [0.3, 1], 0.6, ANY_ALT, [0.7, 1.1]),
    T('crystal_cluster_shrub', 'shrub', [0, 0.5], [0, 0.5], 1.0, ANY_ALT, [0.6, 1.0]),
    T('facet_fern', 'herb', [0.4, 1], [0.35, 1], 0.7, ANY_ALT, [0.6, 1.0]),
    T('crystal_flower', 'herb', [0.2, 0.7], [0, 0.4], 0.6, ANY_ALT, [0.6, 1.0]),
    T('glasspetal_bloom', 'herb', [0.3, 0.8], [0, 0.5], 0.6, ANY_ALT, [0.6, 1.0]),
    T('prism_moss_clump', 'moss', [0.5, 1], [0.4, 1], 0.9, ANY_ALT, [0.6, 1.0]),
  ],
  dune_desert: [
    T('needle_tumbleweed', 'shrub', [0, 0.35], [0, 0.3], 0.5, ANY_ALT, [0.6, 1.0]),
    T('sandspire_yucca', 'succulent', [0, 0.4], [0, 0.3], 0.6, ANY_ALT, [0.7, 1.1]),
    T('desert_succulent_a', 'succulent', [0, 0.3], [0, 0.3], 0.6, ANY_ALT, [0.6, 1.0]),
    T('desert_succulent_b', 'succulent', [0, 0.35], [0, 0.4], 0.6, ANY_ALT, [0.6, 1.0]),
    T('barrel_cactus_alien', 'succulent', [0, 0.25], [0, 0.2], 0.5, ANY_ALT, [0.6, 1.0]),
    T('ember_aloe', 'succulent', [0.05, 0.45], [0, 0.4], 0.6, ANY_ALT, [0.6, 1.0]),
    T('dune_grass_tuft', 'herb', [0.05, 0.6], [0, 0.3], 0.5, ANY_ALT, [0.6, 1.0]),
    T('sand_rose_cluster', 'herb', [0, 0.3], [0, 0.2], 0.7, ANY_ALT, [0.6, 1.0]),
  ],
  rocky_highlands: [
    T('cliff_juniper_shrub', 'shrub', [0.1, 0.6], [0, 0.9], 1.1, ANY_ALT, [0.7, 1.2]),
    T('scree_heather', 'shrub', [0.1, 0.6], [0, 0.4], 1.0, ANY_ALT, [0.6, 1.0]),
    T('summit_thistle', 'herb', [0, 0.5], [0, 0.3], 0.9, [60, 600], [0.6, 1.0]),
    T('fern_bush', 'herb', [0.4, 1], [0.35, 1], 0.8, ANY_ALT, [0.6, 1.0]),
    T('bristle_grass', 'herb', [0, 0.6], [0, 0.35], 0.9, ANY_ALT, [0.6, 1.0]),
    T('ledge_moss_cushion', 'moss', [0.3, 1], [0, 0.8], 1.2, ANY_ALT, [0.6, 1.0]),
    T('boulder_lichen_mound', 'moss', [0.2, 0.8], [0, 0.6], 1.0, ANY_ALT, [0.6, 1.0]),
  ],
  moss_wetland: [
    T('coral_shrub', 'shrub', [0.4, 1], [0, 0.95], 0.6, ANY_ALT, [0.7, 1.1]),
    T('bog_lantern_plant', 'shrub', [0.6, 1], [0, 0.7], 0.5, ANY_ALT, [0.7, 1.1]),
    T('lumen_grass', 'herb', [0.5, 1], [0, 0.5], 0.6, ANY_ALT, [0.6, 1.0]),
    T('dew_cup_plant', 'herb', [0.6, 1], [0.2, 0.9], 0.5, ANY_ALT, [0.6, 1.0]),
    T('mist_orchid', 'herb', [0.5, 1], [0.3, 1], 0.6, ANY_ALT, [0.6, 1.0]),
    T('moss_mat', 'moss', [0.5, 1], [0.2, 1], 0.7, ANY_ALT, [0.8, 1.3]),
    T('peat_moss_hummock', 'moss', [0.6, 1], [0, 0.7], 0.4, ANY_ALT, [0.7, 1.1]),
    T('bubble_kelp_mound', 'moss', [0.7, 1], [0, 0.6], 0.4, ANY_ALT, [0.6, 1.0]),
  ],
  fungal_basin: [
    T('spore_pod_bush', 'shrub', [0.3, 0.9], [0, 0.95], 0.7, ANY_ALT, [0.7, 1.1]),
    T('glow_shrub', 'shrub', [0.4, 1], [0.2, 1], 0.6, ANY_ALT, [0.7, 1.1]),
    T('spore_veil_grass', 'herb', [0.3, 0.9], [0, 0.5], 0.7, ANY_ALT, [0.6, 1.0]),
    T('ink_cap_cluster', 'fungus', [0.4, 1], [0.3, 1], 0.6, ANY_ALT, [0.6, 1.0]),
    T('shelf_fungus_stack', 'fungus', [0.4, 1], [0.4, 1], 0.8, ANY_ALT, [0.6, 1.0]),
    T('giant_puffball_cluster', 'fungus', [0.3, 0.9], [0, 0.6], 0.5, ANY_ALT, [0.6, 1.0]),
    T('coral_fungus_bloom', 'fungus', [0.4, 1], [0.2, 0.9], 0.6, ANY_ALT, [0.6, 1.0]),
  ],
};
/** Riparian species every biome shares: reeds at the water's edge, shrubs a little further back. */
export const RIPARIAN_REEDS: PlantTraits[] = [
  T('tall_reed', 'reed', [0.6, 1], [0, 0.7], 0.4, [-5, 600], [0.8, 1.2]),
  T('azure_reed_cluster', 'reed', [0.6, 1], [0, 0.7], 0.4, [-5, 600], [0.8, 1.2]),
  T('marsh_helix_reed', 'reed', [0.7, 1], [0, 0.6], 0.3, [-5, 600], [0.8, 1.2]),
];
export const RIPARIAN_SHRUBS: PlantTraits[] = [
  T('berry_bush', 'shrub', [0.4, 1], [0, 0.8], 0.6, ANY_ALT, [0.8, 1.2]),
  T('glow_shrub', 'shrub', [0.5, 1], [0, 0.9], 0.6, ANY_ALT, [0.8, 1.2]),
  T('coral_shrub', 'shrub', [0.5, 1], [0, 0.7], 0.6, ANY_ALT, [0.8, 1.2]),
];
/** Forest-floor species any wood carries (ferns and mushrooms under a canopy, whatever the biome). */
export const FOREST_FLOOR: PlantTraits[] = [
  T('fern_bush', 'herb', [0.3, 1], [0.45, 1], 0.8, ANY_ALT, [0.7, 1.1]),
  T('facet_fern', 'herb', [0.3, 1], [0.45, 1], 0.8, ANY_ALT, [0.7, 1.1]),
  T('ink_cap_cluster', 'fungus', [0.35, 1], [0.55, 1], 0.6, ANY_ALT, [0.5, 0.9]),
  T('shelf_fungus_stack', 'fungus', [0.35, 1], [0.6, 1], 0.7, ANY_ALT, [0.5, 0.9]),
];

/** Where a plant stands: the niche axes. */
export interface EcoSite { wet: number; shade: number; slope: number; alt: number; waterDist: number }

/** Smooth band membership: 1 inside [lo, hi], falling to 0 over `soft` outside. */
export function band(x: number, lo: number, hi: number, soft = 0.15): number {
  if (x >= lo && x <= hi) return 1;
  const d = x < lo ? lo - x : x - hi;
  const t = Math.max(0, 1 - d / soft);
  return t * t * (3 - 2 * t);
}
/** Viability 0..1 of a species at a site (the product of its niche memberships). */
export function viability(t: PlantTraits, s: EcoSite): number {
  const slope = s.slope >= t.slopeMax ? 0 : 1 - Math.pow(s.slope / t.slopeMax, 3);
  return band(s.wet, t.moist[0], t.moist[1]) * band(s.shade, t.shade[0], t.shade[1]) * slope * band(s.alt, t.alt[0], t.alt[1], 20);
}
/**
 * The best-suited species at a site: the highest viability wins, a small per-candidate jitter (`r`
 * in [0, 1)) breaking near-ties so a meadow mixes its two best species rather than one. Null when
 * nothing is viable above `floor`.
 */
export function pickSpecies(list: readonly PlantTraits[], s: EcoSite, r: number, floor = 0.08): { t: PlantTraits; v: number } | null {
  let best: PlantTraits | null = null, bv = floor;
  for (let i = 0; i < list.length; i++) {
    const v = viability(list[i], s);
    const score = v * (0.82 + 0.36 * ((r * 7.13 + i * 0.618) % 1));
    if (score > bv) { bv = score; best = list[i]; }
  }
  return best ? { t: best, v: viability(best, s) } : null;
}
/** A plant's scale: its trait range × viability (the niche's edge grows small plants). */
export function viableScale(t: PlantTraits, v: number, r: number): number {
  const s = t.scale[0] + (t.scale[1] - t.scale[0]) * r;
  return s * (0.55 + 0.45 * Math.min(1, v));
}

/**
 * CLUSTERS: cluster seeds on a 14 m jittered lattice × a 90 m low-frequency noise → 0..1. Plants
 * accept a candidate with this probability (clumps where the seeds sit, gaps between).
 */
export function clusterField(seed: string, x: number, z: number, salt = 'eco'): number {
  const lo = forestNoise(seed, `${salt}Lo`, x, z, 90);
  const cx = Math.floor(x / 14), cz = Math.floor(z / 14);
  let best = 0;
  for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
    const gx = cx + i, gz = cz + j;
    const sx = (gx + 0.2 + 0.6 * forestNoise(seed, `${salt}Sx`, gx * 14 + 0.5, gz * 14 + 0.5, 1)) * 14;
    const sz = (gz + 0.2 + 0.6 * forestNoise(seed, `${salt}Sz`, gx * 14 + 0.5, gz * 14 + 0.5, 1)) * 14;
    const rad = 3 + 5 * forestNoise(seed, `${salt}Sr`, gx * 14 + 0.5, gz * 14 + 0.5, 1);
    const d = Math.hypot(x - sx, z - sz);
    const w = Math.max(0, 1 - d / rad);
    if (w > best) best = w;
  }
  return Math.min(1, best * (0.35 + 0.95 * lo));
}

/** CLEARINGS: holes 20–80 m across inside deep stands (1 = open clearing, 0 = wood). */
export function clearingAt(seed: string, x: number, z: number, forestDensity: number): number {
  if (forestDensity < 0.55) return 0;
  const n = 0.7 * forestNoise(seed, 'clearing', x, z, 70) + 0.3 * forestNoise(seed, 'clearing2', x, z, 23);
  const t = Math.min(1, Math.max(0, (n - 0.7) / 0.1));
  return t * t * (3 - 2 * t) * Math.min(1, (forestDensity - 0.55) / 0.2);
}

/**
 * Shrub density per hectare by position in the woods (§3b): deep forest 300–600, the EDGE (the
 * ecotone band, woods density 0.1–0.5) 600–1,000, open ground 50–200, riparian (6–25 m from water)
 * 400–800. `n` ∈ [0, 1) picks inside each band.
 */
export function shrubDensityPerHa(forest: number, waterDist: number, n: number): number {
  if (waterDist >= 6 && waterDist <= 25) return 400 + 400 * n;
  if (forest >= 0.1 && forest <= 0.5) return 600 + 400 * n;
  if (forest > 0.5) return 300 + 300 * n;
  return 50 + 150 * n;
}
/** Wetness proxy 0..1 for the understory: water proximity, concavity, the biome's own climate. */
export function ecoWetness(waterDist: number, laplace: number, wetlandW: number, desertW: number): number {
  const byWater = waterDist < 40 ? 1 - waterDist / 40 : 0;
  const hollow = Math.max(0, Math.min(1, laplace / 0.006));
  return Math.max(0, Math.min(1, 0.25 + 0.5 * byWater + 0.25 * hollow + 0.45 * wetlandW - 0.35 * desertW));
}
